import React, { useEffect, useState } from 'react';
import { formatDate } from './dateFormat';
import { authFetch, userMoveInDepositUrl } from './apiClient';

const METHOD_LABELS = { CASH: 'Cash', UPI: 'UPI', BANK_TRANSFER: 'Bank transfer' };

export function depositPaidBy(entry) {
  if (entry.source === 'razorpay') return 'Online';
  return METHOD_LABELS[entry.method] || 'Manual';
}

// Admin view of one tenant's whole deposit ledger: every entry with how it was paid,
// its Razorpay payment id or UPI/bank reference, and who recorded it.
export default function DepositHistoryModal({ username, onClose }) {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await authFetch(userMoveInDepositUrl.self(username));
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        if (!cancelled) setSummary(data);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load deposit history');
      }
    })();
    return () => { cancelled = true; };
  }, [username]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="deposit-history-title"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000, padding: 16 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ background: '#fff', borderRadius: 14, padding: 20, width: '100%', maxWidth: 820, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.3)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 12 }}>
          <h3 id="deposit-history-title" style={{ margin: 0, color: 'var(--ink)' }}>Deposit history · {username}</h3>
          <button
            autoFocus
            onClick={onClose}
            aria-label="Close"
            style={{ background: 'var(--line)', color: 'var(--ink-soft)', border: 'none', borderRadius: 8, padding: '6px 12px', fontWeight: 'bold', cursor: 'pointer' }}
          >✕</button>
        </div>

        {error ? (
          <p style={{ color: '#b91c1c' }}>{error}</p>
        ) : !summary ? (
          <p style={{ color: 'var(--muted)' }}>Loading…</p>
        ) : (
          <>
            <div style={{ fontSize: 14, color: 'var(--ink-soft)', marginBottom: 12 }}>
              Paid <strong>₹{summary.totalAmountDeposited}</strong>
              {summary.demandedDeposit != null && <> of ₹{summary.demandedDeposit} demanded
                {summary.totalAmountDeposited >= summary.demandedDeposit
                  ? <span style={{ color: '#16a34a', fontWeight: 600 }}> · fully paid</span>
                  : <span style={{ color: '#dc2626', fontWeight: 600 }}> · ₹{(summary.demandedDeposit - summary.totalAmountDeposited).toFixed(2)} remaining</span>}
              </>}
            </div>
            {summary.history.length === 0 ? (
              <p style={{ color: 'var(--muted)' }}>No deposit payments yet.</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>{['Paid / Received', 'Amount', 'Paid by', 'Payment ID / Ref', 'Recorded by', 'Notes'].map(h => <th key={h} style={th}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {summary.history.map((e, i) => (
                      <tr key={e.id} style={{ background: i % 2 ? '#fff' : 'var(--surface)' }}>
                        <td style={td}>{e.paidDate ? formatDate(e.paidDate) : '—'}</td>
                        <td style={td}>₹{e.amount}</td>
                        <td style={td}>{depositPaidBy(e)}</td>
                        <td style={td}>{e.paymentId || e.reference || '—'}</td>
                        <td style={td}>{e.source === 'razorpay' ? 'Automatic' : (e.recordedBy || '—')}</td>
                        <td style={td}>{e.notes || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

const th = { padding: 8, background: 'var(--grad-primary)', color: '#fff', textAlign: 'center', fontSize: 12 };
const td = { padding: 6, textAlign: 'center', fontSize: 13, borderBottom: '1px solid var(--line)' };
